/**
 * Utility formatters for money, dates, etc.
 * 
 * Money convention: API returns prices in rupees (float), NOT paise.
 */

/**
 * Format rupees (float) for display with 2 decimal places
 * @example rupeesToDisplay(20) => "20.00"
 * @example rupeesToDisplay(20.5) => "20.50"
 */
export function rupeesToDisplay(rupees: number | undefined | null): string {
  if (rupees == null || isNaN(rupees)) return "0.00";
  return rupees.toFixed(2);
}

/**
 * Format money with the right currency symbol.
 *
 * `currency` is an ISO 4217 code (INR, QAR, SAR). Omit it and you get rupees,
 * which is what every existing caller relies on — the parameter is additive so
 * the ~27 files using this keep working unchanged.
 *
 * Accounting must always pass one (#417). `ledger_entries.currency` is stamped
 * from the outlet, so display currency has to come from the same place or the
 * books say one thing and the screen says another. A Qatari outlet reading its
 * balance sheet in rupees is the bug this fixes.
 *
 * @example formatMoney(20) => "₹20.00"
 * @example formatMoney(20, "QAR") => "QAR 20.00"
 */
export function formatMoney(rupees: number | undefined | null, currency?: string): string {
  if (rupees == null || isNaN(rupees)) return currency ? formatMoney(0, currency) : "₹0.00";
  if (!currency || currency === "INR") return `₹${rupeesToDisplay(rupees)}`;
  try {
    // No explicit fraction digits: Intl already uses the ISO 4217 minor-unit
    // count for the currency. Forcing 2 would pad JPY (0 minor units) and
    // truncate BHD/KWD (3). Nothing we ship today is affected — INR and QAR are
    // both 2 — but hard-coding it would be a trap for whoever adds the first
    // currency that is not.
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(rupees);
  } catch {
    // Intl throws on a code it does not know. Showing the code beside the
    // number is wrong-looking but honest; falling back to ₹ would be wrong.
    return `${currency} ${rupeesToDisplay(rupees)}`;
  }
}

/**
 * A calendar day as YYYY-MM-DD, in the reader's own timezone.
 *
 * Not toISOString(): that converts to UTC first, so east of Greenwich every
 * time before the offset falls on the day before. In India a report asked for
 * "today" before 05:30 was asked for yesterday.
 */
export function localDateISO(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * Format a date string for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a calendar date: a DATE column such as an order, due or business date.
 *
 * The API sends a DATE as midnight UTC ("2026-09-20T00:00:00Z"). formatDate
 * reads that as an instant, which west of UTC is the evening before, so an
 * order dated the 20th showed as the 19th. Only the YYYY-MM-DD part is read
 * here, and the day is built in local time, so it is the same day everywhere.
 * Keep formatDate and formatDateTime for real timestamps (created_at).
 *
 * A value without a YYYY-MM-DD start is returned as it came.
 */
export function formatCalendarDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a date string with time
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * The time of day `iso` fell at in `timeZone`, e.g. "8:23 pm". When that
 * moment's calendar date is not `businessDate` (a run recorded at 12:30 am
 * that belongs to the day before), the date is added: "12:30 am, 23 Sep".
 */
export function formatTimeOn(iso: string, timeZone: string, businessDate: string): string {
  const at = new Date(iso);
  const time = at.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", timeZone });
  if (businessDateForInput(at, timeZone) === businessDate) return time;
  return `${time}, ${at.toLocaleDateString("en-IN", { day: "numeric", month: "short", timeZone })}`;
}

/**
 * A date for an input field (YYYY-MM-DD), in the reader's own timezone.
 *
 * It used to go through UTC, which east of Greenwich hands back yesterday for
 * any time before the offset: in India a page opened before 05:30 defaulted its
 * filters to the day before and showed an empty period. A date input is a
 * calendar day as the person in front of it counts days, never a UTC instant.
 *
 * For a timestamp that came from the server, keep reading it as UTC — a
 * business_date arrives as midnight UTC, and formatting that locally would move
 * it a day west of Greenwich.
 */
export function formatDateForInput(date: Date): string {
  return localDateISO(date);
}

/**
 * The calendar date `now` falls on in `timeZone`, as YYYY-MM-DD.
 *
 * Unlike formatDateForInput above, this does not convert to UTC first. A user in New
 * York opening a report at 8pm would otherwise land on tomorrow's date and see an empty
 * day.
 *
 * The parts are read out and joined here rather than trusting a formatted string. No
 * locale's *layout* is contractual — en-CA renders ISO-shaped today, but that is CLDR
 * data, not a guarantee, and this value goes straight into an API query parameter the
 * server parses as YYYY-MM-DD. A separator or ordering change in some browser's ICU
 * would turn that into a silently wrong request. The locale tag also pins the Gregorian
 * calendar and Latin digits, so a viewer whose environment defaults elsewhere does not
 * get a Buddhist year or Arabic-Indic numerals in a date the API must parse.
 *
 * The outlet's own zone is what the server stamps business_date in, and the browser's
 * zone is only an approximation of it — but it is the approximation the user is standing
 * in, and it beats UTC for everyone not living there.
 */
export function businessDateForInput(now: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US-u-ca-gregory-nu-latn", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);

  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";

  const year = value("year").padStart(4, "0");
  const month = value("month").padStart(2, "0");
  const day = value("day").padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** The time zone the browser is running in, for business-date defaults. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * Format a business_date string (may arrive as "2026-04-24T00:00:00Z" or "2026-04-24")
 * using the outlet's date_format setting.
 */
export function formatBusinessDate(dateStr: string, dateFormat?: string): string {
  const datePart = dateStr.split("T")[0]; // "2026-04-24"
  const [year, month, day] = datePart.split("-");
  switch (dateFormat) {
    case "MM/DD/YYYY": return `${month}/${day}/${year}`;
    case "YYYY-MM-DD": return datePart;
    default:           return `${day}/${month}/${year}`; // "DD/MM/YYYY"
  }
}

/**
 * Parse a date from input field
 */
export function parseDateInput(value: string): string {
  return value; // Already in YYYY-MM-DD format
}

/**
 * Format percentage
 */
export function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

/**
 * Format quantity with unit, stripping trailing zeros for cleaner display
 */
export function formatQuantity(qty: number, unit: string): string {
  return `${Number(qty.toFixed(3))} ${unit}`;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

/**
 * Format phone number (Indian format)
 */
export function formatPhone(phone?: string): string {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  return phone;
}
